import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { AppHeader } from "../components/AppHeader";
import { useAuth } from "../lib/auth";
import { IncidentsBulletin } from "../components/IncidentsBulletin";
import { toast, Toaster } from "sonner";
import { ArrowLeft, Plus, Trash2, AlertTriangle, Backpack, UserX } from "lucide-react";

export const COMPORTEMENT_MOTIFS = [
  "Insolence",
  "Bavardage",
  "Violence verbale",
  "Violence physique",
  "Refus de travail",
  "Perturbation du cours",
  "Non-respect du matériel",
  "Autre",
];

const inputCls = "w-full px-4 py-2.5 bg-white border border-[var(--parchment-2)] focus:border-[var(--maroon)] outline-none text-sm";

export default function Signalements() {
  const { user } = useAuth();
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [students, setStudents] = useState([]);
  const [mine, setMine] = useState([]);
  const [form, setForm] = useState({
    class_id: "",
    subject_id: "",
    selected: {},
    type: "travail_non_fait",
    sub_motif: COMPORTEMENT_MOTIFS[0],
    comment: "",
    date: new Date().toISOString().slice(0, 10),
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirmId, setConfirmId] = useState(null);

  const loadMine = useCallback(async () => {
    const { data } = await api.get("/incidents/mine");
    setMine(data);
  }, []);

  useEffect(() => {
    Promise.all([api.get("/classes"), api.get("/subjects")]).then(([c, s]) => {
      setClasses(c.data);
      setSubjects(s.data);
      setForm((f) => ({ ...f, class_id: c.data[0]?.id || "", subject_id: s.data[0]?.id || "" }));
    });
    loadMine();
  }, [loadMine]);

  useEffect(() => {
    if (!form.class_id) return;
    api.get(`/classes/${form.class_id}/students`).then(({ data }) => setStudents(data));
    setForm((f) => ({ ...f, selected: {} }));
  }, [form.class_id]);

  const toggleStudent = (id) => {
    setForm((f) => ({ ...f, selected: { ...f.selected, [id]: !f.selected[id] } }));
  };

  const submit = async () => {
    const student_ids = Object.keys(form.selected).filter((k) => form.selected[k]);
    if (!student_ids.length) {
      toast.error("Cochez au moins un élève");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/incidents", {
        class_id: form.class_id,
        subject_id: form.subject_id || null,
        student_ids,
        type: form.type,
        sub_motif: form.type === "comportement" ? form.sub_motif : null,
        comment: form.comment,
        date: form.date,
      });
      toast.success(`Signalement enregistré pour ${student_ids.length} élève${student_ids.length > 1 ? "s" : ""} — la direction sera notifiée`);
      setForm((f) => ({ ...f, selected: {}, comment: "" }));
      await loadMine();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/incidents/${id}`);
      setConfirmId(null);
      toast.success("Signalement supprimé");
      await loadMine();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const selectedCount = Object.values(form.selected).filter(Boolean).length;

  return (
    <div className="min-h-screen bg-[var(--cream)]">
      <AppHeader />
      <Toaster richColors position="top-right" />
      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <Link to="/teacher" className="inline-flex items-center gap-2 text-sm text-[var(--ink-soft)] hover:text-[var(--maroon)] transition-colors" data-testid="signalements-back">
          <ArrowLeft size={16} /> Retour au tableau de bord
        </Link>

        <div className="mt-6 pb-8 border-b border-[var(--parchment-2)]">
          <div className="eyebrow">Signalements · Travail non fait, affaires oubliées & comportement</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl font-semibold mt-2">Signaler un problème</h1>
          <p className="mt-3 text-[var(--ink-soft)] max-w-2xl">
            Notez quand un élève n'a pas fait son travail, n'a pas ses affaires, ou pose un problème de comportement.
            La direction est immédiatement notifiée par email.
          </p>
        </div>

        {user?.role === "assoc_admin" && <IncidentsBulletin />}

        <div className="mt-8 grid lg:grid-cols-2 gap-6">
          <div className="bg-white border border-[var(--parchment-2)] p-6" data-testid="signalement-form">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="eyebrow block mb-1">Classe</label>
                <select className={inputCls} value={form.class_id} onChange={(e) => setForm((f) => ({ ...f, class_id: e.target.value }))} data-testid="incident-class-select">
                  {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="eyebrow block mb-1">Matière</label>
                <select className={inputCls} value={form.subject_id} onChange={(e) => setForm((f) => ({ ...f, subject_id: e.target.value }))} data-testid="incident-subject-select">
                  {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            </div>

            <div className="mt-4">
              <label className="eyebrow block mb-1">Type de problème</label>
              <div className="flex flex-wrap gap-2" data-testid="incident-type-toggle">
                <TypeBtn active={form.type === "travail_non_fait"} icon={<AlertTriangle size={14} />} label="Travail non fait" onClick={() => setForm((f) => ({ ...f, type: "travail_non_fait" }))} testId="type-travail" />
                <TypeBtn active={form.type === "affaires_oubliees"} icon={<Backpack size={14} />} label="Affaires oubliées" onClick={() => setForm((f) => ({ ...f, type: "affaires_oubliees" }))} testId="type-affaires" />
                <TypeBtn active={form.type === "comportement"} icon={<UserX size={14} />} label="Comportement" onClick={() => setForm((f) => ({ ...f, type: "comportement" }))} testId="type-comportement" />
              </div>
            </div>

            {form.type === "comportement" && (
              <div className="mt-4">
                <label className="eyebrow block mb-1">Motif du comportement</label>
                <select className={inputCls} value={form.sub_motif} onChange={(e) => setForm((f) => ({ ...f, sub_motif: e.target.value }))} data-testid="incident-submotif-select">
                  {COMPORTEMENT_MOTIFS.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            )}

            <div className="mt-4">
              <label className="eyebrow block mb-1">Élèves concernés ({selectedCount} sélectionné{selectedCount > 1 ? "s" : ""})</label>
              <div className="max-h-52 overflow-y-auto border border-[var(--parchment-2)] divide-y divide-[var(--parchment-2)]" data-testid="incident-students-list">
                {students.map((s) => (
                  <label key={s.id} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-[var(--parchment)]/50">
                    <input type="checkbox" checked={!!form.selected[s.id]} onChange={() => toggleStudent(s.id)} className="accent-[var(--maroon)] w-4 h-4" data-testid={`incident-student-check-${s.id}`} />
                    <span>{s.last_name.toUpperCase()} {s.first_name}</span>
                  </label>
                ))}
                {students.length === 0 && <div className="px-3 py-3 text-sm text-[var(--ink-soft)]">Aucun élève.</div>}
              </div>
            </div>

            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              <div>
                <label className="eyebrow block mb-1">Date</label>
                <input type="date" className={inputCls} value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} data-testid="incident-date-input" />
              </div>
            </div>

            <div className="mt-4">
              <label className="eyebrow block mb-1">Commentaire (facultatif)</label>
              <textarea rows={2} className={inputCls} value={form.comment} onChange={(e) => setForm((f) => ({ ...f, comment: e.target.value }))} placeholder="Ex. Exercices 3 à 5 non faits pour la 2e fois" data-testid="incident-comment-input" />
            </div>

            <button className="btn-primary mt-6 w-full justify-center" onClick={submit} disabled={submitting} data-testid="incident-submit-btn">
              <Plus size={16} /> {submitting ? "Envoi..." : "Enregistrer le signalement"}
            </button>
          </div>

          <div className="bg-white border border-[var(--parchment-2)] p-6" data-testid="my-incidents">
            <div className="pb-3 border-b border-[var(--parchment-2)]">
              <div className="eyebrow">Mes signalements</div>
              <h2 className="font-serif-display text-2xl font-semibold mt-1">{mine.length} signalement{mine.length > 1 ? "s" : ""}</h2>
            </div>
            <div className="mt-3 space-y-3 max-h-[600px] overflow-y-auto">
              {mine.map((r) => (
                <div key={r.id} className="border border-[var(--parchment-2)] p-4" data-testid={`my-incident-card-${r.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="font-medium">{r.student?.last_name.toUpperCase()} {r.student?.first_name}</span>
                      <span className="text-[var(--ink-soft)]">{r.class?.name}</span>
                      <TypeBadge type={r.type} label={r.type_label} />
                    </div>
                    {confirmId === r.id ? (
                      <button className="!py-1 !px-2 text-xs bg-[var(--late)] text-white" onClick={() => remove(r.id)} data-testid={`confirm-delete-incident-${r.id}`}>Confirmer ?</button>
                    ) : (
                      <button className="text-[var(--muted)] hover:text-[var(--late)]" onClick={() => setConfirmId(r.id)} data-testid={`delete-incident-${r.id}`}>
                        <Trash2 size={15} />
                      </button>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-[var(--muted)] font-mono-spec">{r.date} · {r.subject?.name || "—"}</div>
                  {r.comment && <div className="mt-1.5 text-sm text-[var(--ink-soft)]">{r.comment}</div>}
                  {r.sanction_text && (
                    <div className="mt-2 text-sm bg-[var(--parchment)] border-l-2 border-[var(--maroon)] px-3 py-2">
                      <span className="eyebrow !text-[var(--maroon)]">Direction :</span> {r.sanction_text}
                    </div>
                  )}
                </div>
              ))}
              {mine.length === 0 && <div className="py-4 text-sm text-[var(--ink-soft)]">Aucun signalement pour le moment.</div>}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function TypeBtn({ active, icon, label, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium border transition ${
        active ? "bg-[var(--maroon)] text-white border-[var(--maroon)]" : "bg-white text-[var(--ink-soft)] border-[var(--parchment-2)] hover:border-[var(--ink)]"
      }`}
    >
      {icon} {label}
    </button>
  );
}

export function TypeBadge({ type, label }) {
  const colors = { travail_non_fait: "var(--late)", affaires_oubliees: "var(--just)", comportement: "var(--maroon)" };
  return (
    <span
      className="px-2 py-0.5 text-xs font-medium text-white"
      style={{ background: colors[type] || "var(--maroon)" }}
    >
      {label}
    </span>
  );
}
